import { useState, useEffect, useCallback } from "react";
import { Form, Spinner } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import moment from "moment";

const BASE = "https://dailydish.in";
const SALES_API = `${BASE}/api/admin/plan/sales-tracker`;
const PRODUCTS_API = `${BASE}/api/production/products`;
const LOG_API = `${BASE}/api/production/log`;
const UPLOAD_API = `${BASE}/api/production/log/upload-photo`;
const CHEFS_API = `${BASE}/api/production/chefs`;

const SESSION_LABEL = { b: "Breakfast", l: "Lunch", d: "Dinner" };
const SESSION_BTN = {
  b: {
    active: {
      background: "#FAEEDA",
      color: "#BA7517",
      border: "1.5px solid #EF9F27",
    },
  },
  l: {
    active: {
      background: "#E1F5EE",
      color: "#085041",
      border: "1.5px solid #1D9E75",
    },
  },
  d: {
    active: {
      background: "#E6F1FB",
      color: "#185FA5",
      border: "1.5px solid #185FA5",
    },
  },
};
const TYPE_UNIT = { weight: "kg", pieces: "pcs", pack: "packs" };
const THRESH = { weight: 0.005, pieces: 0.5, pack: 0.5 };

function fmtQty(val, type) {
  if (val === null || val === undefined || val === "") return "—";
  if (type === "weight") return `${parseFloat(val).toFixed(2)} kg`;
  if (type === "pieces") return `${parseFloat(val)} pcs`;
  return `${parseFloat(val)} packs`;
}

function deriveStatus(target, state, type) {
  if (!state || state.status === "pending") return "pending";
  if (["open", "recook"].includes(state.status)) return state.status;
  const thresh = THRESH[type] || 0.5;
  const cooked = state.totalCooked || 0;
  if (!cooked) return "pending";
  const diff = cooked - target;
  if (Math.abs(diff) <= thresh) return "ready";
  if (diff < 0) return "short";
  return "excess";
}

const STATUS_STYLE = {
  pending: { bg: "#f0f0f0", color: "#666", label: "Pending" },
  open: { bg: "#E6F1FB", color: "#185FA5", label: "In Progress" },
  ready: { bg: "#E1F5EE", color: "#085041", label: "✓ Ready" },
  short: { bg: "#FCEBEB", color: "#A32D2D", label: "⚠ Shortage" },
  excess: { bg: "#FEF3E7", color: "#994010", label: "↑ Excess" },
  recook: { bg: "#FAEEDA", color: "#BA7517", label: "↻ Recooking" },
};

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pending;
  return (
    <span
      style={{
        fontSize: 12,
        fontWeight: 600,
        padding: "4px 12px",
        borderRadius: 20,
        background: s.bg,
        color: s.color,
      }}
    >
      {s.label}
    </span>
  );
}

const CARD_BORDER = {
  ready: "#1D9E75",
  short: "#E24B4A",
  excess: "#EF9F27",
  recook: "#EF9F27",
};

const BATCH_RESULT_LABEL = {
  short: "⚠ Shortage",
  excess: "↑ Excess",
  ready: "✓ Ready",
};
const BATCH_RESULT_COLOR = {
  short: "#A32D2D",
  excess: "#994010",
  ready: "#085041",
};

function BatchHistory({ batches, type, chefs }) {
  if (!batches || batches.length === 0) return null;
  return (
    <div style={{ borderTop: "1px solid #f0f0f0", background: "#fafafa" }}>
      <div
        style={{
          padding: "10px 20px 4px",
          fontSize: 11,
          fontWeight: 700,
          color: "#aaa",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        Cooking Log
      </div>
      {batches.map((b, i) => {
        const resultColor = BATCH_RESULT_COLOR[b.resultStatus] || "#888";
        const resultLabel = BATCH_RESULT_LABEL[b.resultStatus] || "";
        return (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "10px 20px",
              borderBottom:
                i < batches.length - 1 ? "1px solid #f0f0f0" : "none",
            }}
          >
            {/* Batch number bubble */}
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: "#e8e8e8",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: 700,
                color: "#555",
                flexShrink: 0,
              }}
            >
              {b.batchNum}
            </div>
            {/* Details */}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>
                {b.batchNum === 1
                  ? "Initial batch"
                  : `Recook #${b.batchNum - 1}`}
                <span style={{ fontWeight: 400, color: "#555", marginLeft: 8 }}>
                  {fmtQty(b.qty, type)}
                </span>
              </div>
              <div style={{ fontSize: 12, color: "#aaa", marginTop: 2 }}>
                {b.time && <span>{b.time}</span>}
                {b.chefId && (
                  <span>
                    {" "}
                    ·{" "}
                    {typeof b.chefId === "object"
                      ? b.chefId.name
                      : chefs.find((c) => c._id === b.chefId)?.name || ""}
                  </span>
                )}
              </div>
            </div>
            {/* Batch photos */}
            {(b.images || []).length > 0 && (
              <div style={{ display: "flex", gap: 4 }}>
                {b.images.slice(0, 2).map((img, j) => (
                  <img
                    key={j}
                    src={img}
                    alt=""
                    onClick={() => window.open(img, "_blank")}
                    style={{
                      width: 36,
                      height: 36,
                      objectFit: "cover",
                      borderRadius: 6,
                      border: "1px solid #e8e8e8",
                      cursor: "pointer",
                    }}
                  />
                ))}
              </div>
            )}
            {/* Result */}
            {resultLabel && (
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: resultColor,
                  flexShrink: 0,
                }}
              >
                {resultLabel}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ProductionCard({ entry, chefs, onUpdate }) {
  const { cookableItem: ci, target, log: state } = entry;
  const unit = TYPE_UNIT[ci.outputType];
  const thresh = THRESH[ci.outputType];

  const [panelOpen, setPanelOpen] = useState(false);
  const [qty, setQty] = useState("");
  const [chefId, setChefId] = useState("");
  const [tempImg, setTempImg] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPanelOpen(
      ["open", "recook"].includes(state.status) && !state.pendingAlert,
    );
    setChefId(state.chefId?._id || state.chefId || "");
    setQty(
      state.status === "recook"
        ? String(parseFloat(state.pendingDiff || 0).toFixed(2))
        : "",
    );
    setTempImg(null);
  }, [state.status, state.chefId, state.pendingDiff, state.pendingAlert]);

  const save = (update) => {
    setSaving(true);
    return onUpdate(update).finally(() => setSaving(false));
  };

  const handleOpen = () =>
    save({ ...state, status: "open", pendingAlert: null });

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("photo", file);
      const res = await axios.post(UPLOAD_API, fd);
      if (res.data.success) {
        setTempImg(res.data.url);
        toast.success("Photo uploaded");
      }
    } catch {
      toast.error("Photo upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleConfirm = () => {
    const raw = parseFloat(qty);
    if (!raw || raw <= 0) return toast.error("Enter quantity cooked");
    const newTotal = (state.totalCooked || 0) + raw;
    const batchTime = moment().format("HH:mm");
    const batchImages = tempImg ? [tempImg] : [];
    const allImgs = [...(state.images || []), ...batchImages];
    const diff = target - newTotal;
    const absDiff = Math.round(Math.abs(diff) * 1000) / 1000;

    let resultStatus = "ready";
    let newStatus = "ready";
    let pendingAlert = null;
    let pendingDiff = 0;
    if (Math.abs(diff) > thresh) {
      resultStatus = diff > 0 ? "short" : "excess";
      newStatus = "open";
      pendingAlert = resultStatus;
      pendingDiff = absDiff;
    }

    const newBatch = {
      batchNum: (state.batches || []).length + 1,
      qty: raw,
      time: batchTime,
      chefId: chefId || null,
      images: batchImages,
      resultStatus,
    };
    const allBatches = [...(state.batches || []), newBatch];

    return save({
      status: newStatus,
      pendingAlert,
      pendingDiff,
      totalCooked: newTotal,
      images: allImgs,
      readyTime: batchTime,
      chefId,
      batches: allBatches,
    });
  };

  const handleRecook = () =>
    save({
      ...state,
      status: "recook",
      pendingAlert: null,
      recookCount: (state.recookCount || 0) + 1,
    });

  const handleProceed = () =>
    save({ ...state, status: "short", pendingAlert: null });
  const handleExcessLog = () =>
    save({ ...state, status: "excess", pendingAlert: null });

  const derivedStatus = deriveStatus(target, state, ci.outputType);
  const isDone = ["ready", "short", "excess"].includes(derivedStatus);
  const isRecook = state.status === "recook";
  const borderColor = CARD_BORDER[derivedStatus] || "#e8e8e8";
  const stillToCook = Math.max(
    0,
    Math.round((target - (state.totalCooked || 0)) * 1000) / 1000,
  );

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 12,
        border: `1.5px solid ${borderColor}`,
        marginBottom: 12,
        overflow: "hidden",
      }}
    >
      {/* Card header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          padding: "18px 20px",
        }}
      >
        <div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{ci.name}</div>
          {(state.recookCount || 0) > 0 && (
            <div style={{ fontSize: 12, color: "#aaa", marginTop: 2 }}>
              Recooked {state.recookCount}×
            </div>
          )}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexShrink: 0,
          }}
        >
          <div style={{ textAlign: "right" }}>
            <div
              style={{
                fontSize: 22,
                fontWeight: 700,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {fmtQty(target, ci.outputType)}
            </div>
            <div style={{ fontSize: 11, color: "#aaa" }}>to cook</div>
          </div>
          <StatusPill status={derivedStatus} />
          {["pending", "recook"].includes(state.status) &&
            !state.pendingAlert && (
              <button
                onClick={handleOpen}
                disabled={saving}
                style={{
                  padding: "8px 16px",
                  borderRadius: 8,
                  border: "none",
                  background: "#1D9E75",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Set Ready
              </button>
            )}
          {state.status === "open" && !state.pendingAlert && (
            <button
              onClick={handleOpen}
              style={{
                padding: "8px 16px",
                borderRadius: 8,
                border: "1.5px solid #e0e0e0",
                background: "#fff",
                fontWeight: 500,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              Edit
            </button>
          )}
        </div>
      </div>

      {/* Batch history */}
      <BatchHistory
        batches={state.batches}
        type={ci.outputType}
        chefs={chefs}
      />

      {/* Shortage pending alert */}
      {state.pendingAlert === "short" && (
        <div
          style={{
            background: "#FCEBEB",
            borderTop: "1px solid #f5c0c0",
            padding: "14px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div>
            <div style={{ fontWeight: 700, color: "#A32D2D", fontSize: 14 }}>
              ⚠ Shortage: {fmtQty(state.pendingDiff, ci.outputType)} short
            </div>
            <div
              style={{
                fontSize: 13,
                color: "#A32D2D",
                opacity: 0.8,
                marginTop: 2,
              }}
            >
              Target: {fmtQty(target, ci.outputType)} · Cooked so far:{" "}
              {fmtQty(state.totalCooked, ci.outputType)}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={handleRecook}
              style={{
                padding: "8px 16px",
                borderRadius: 8,
                border: "none",
                background: "#E24B4A",
                color: "#fff",
                fontWeight: 600,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              Recook
            </button>
            <button
              onClick={handleProceed}
              style={{
                padding: "8px 16px",
                borderRadius: 8,
                border: "1.5px solid #E24B4A",
                background: "transparent",
                color: "#A32D2D",
                fontWeight: 600,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              Proceed as is
            </button>
          </div>
        </div>
      )}

      {/* Excess pending alert */}
      {state.pendingAlert === "excess" && (
        <div
          style={{
            background: "#FEF3E7",
            borderTop: "1px solid #f0c080",
            padding: "14px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div>
            <div style={{ fontWeight: 700, color: "#994010", fontSize: 14 }}>
              ↑ Excess: {fmtQty(state.pendingDiff, ci.outputType)} extra
            </div>
            <div
              style={{
                fontSize: 13,
                color: "#994010",
                opacity: 0.8,
                marginTop: 2,
              }}
            >
              Target: {fmtQty(target, ci.outputType)} · Cooked:{" "}
              {fmtQty(state.totalCooked, ci.outputType)}
            </div>
          </div>
          <button
            onClick={handleExcessLog}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "none",
              background: "#994010",
              color: "#fff",
              fontWeight: 600,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Log &amp; Done
          </button>
        </div>
      )}

      {/* Input panel */}
      {panelOpen && (
        <div
          style={{
            background: "#fafafa",
            borderTop: "1px solid #f0f0f0",
            padding: 20,
          }}
        >
          {isRecook && (
            <div
              style={{
                background: "#FAEEDA",
                borderRadius: 8,
                padding: "10px 14px",
                marginBottom: 16,
              }}
            >
              <div style={{ fontWeight: 700, color: "#BA7517" }}>
                ↻ Recook #{state.recookCount} — prepare shortage
              </div>
              <div style={{ fontSize: 13, color: "#BA7517", marginTop: 2 }}>
                Still needed:{" "}
                <strong>{fmtQty(stillToCook, ci.outputType)}</strong> — enter
                only the quantity cooking now
              </div>
            </div>
          )}
          <div className="mb-3">
            <label
              style={{
                fontWeight: 500,
                fontSize: 13,
                color: "#555",
                display: "block",
                marginBottom: 6,
              }}
            >
              Assign chef
            </label>
            <Form.Select
              value={chefId}
              onChange={(e) => setChefId(e.target.value)}
              style={{ borderRadius: 8 }}
            >
              <option value="">Select chef</option>
              {chefs.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.name}
                </option>
              ))}
            </Form.Select>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
              marginBottom: 16,
            }}
          >
            <div>
              <label
                style={{
                  fontWeight: 500,
                  fontSize: 13,
                  color: "#555",
                  display: "block",
                  marginBottom: 6,
                }}
              >
                {isRecook ? (
                  <>
                    Qty cooking now{" "}
                    <span style={{ color: "#E24B4A" }}>
                      (need {fmtQty(stillToCook, ci.outputType)})
                    </span>
                  </>
                ) : (
                  "Actual qty cooked"
                )}
              </label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                style={{ borderRadius: 8 }}
              />
            </div>
            <div>
              <label
                style={{
                  fontWeight: 500,
                  fontSize: 13,
                  color: "#555",
                  display: "block",
                  marginBottom: 6,
                }}
              >
                Unit
              </label>
              <Form.Control
                value={unit}
                readOnly
                style={{ borderRadius: 8, color: "#aaa" }}
              />
            </div>
          </div>

          <label
            style={{
              fontWeight: 500,
              fontSize: 13,
              color: "#555",
              display: "block",
              marginBottom: 6,
            }}
          >
            {isRecook ? "Photo of this batch" : "Photo of dish"}
          </label>
          <div
            style={{
              border: "2px dashed #e0e0e0",
              borderRadius: 10,
              padding: 16,
              textAlign: "center",
              background: "#fff",
              marginBottom: 16,
              position: "relative",
              minHeight: 90,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
            }}
          >
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handlePhoto}
              style={{
                position: "absolute",
                inset: 0,
                opacity: 0,
                cursor: "pointer",
                width: "100%",
                height: "100%",
              }}
            />
            {uploading ? (
              <Spinner size="sm" />
            ) : tempImg ? (
              <img
                src={tempImg}
                alt="preview"
                style={{ maxHeight: 120, borderRadius: 8, objectFit: "cover" }}
              />
            ) : (
              <>
                <div style={{ fontSize: 28 }}>📷</div>
                <div style={{ fontSize: 13, color: "#aaa" }}>
                  Tap to open camera or upload
                </div>
              </>
            )}
          </div>

          <button
            onClick={handleConfirm}
            disabled={saving || uploading}
            style={{
              width: "100%",
              padding: "12px",
              borderRadius: 8,
              border: "none",
              background: "#1D9E75",
              color: "#fff",
              fontWeight: 600,
              fontSize: 15,
              cursor: "pointer",
            }}
          >
            {saving ? (
              <Spinner size="sm" />
            ) : isRecook ? (
              "Confirm Batch"
            ) : (
              "Confirm Ready"
            )}
          </button>
        </div>
      )}

      {/* Completion strip */}
      {isDone && (
        <div
          style={{
            borderTop: "1px solid rgba(0,0,0,0.06)",
            padding: "14px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            background:
              derivedStatus === "ready"
                ? "#E1F5EE"
                : derivedStatus === "short"
                  ? "#FCEBEB"
                  : "#FEF3E7",
          }}
        >
          <div>
            <div
              style={{
                fontWeight: 600,
                fontSize: 14,
                color:
                  derivedStatus === "ready"
                    ? "#085041"
                    : derivedStatus === "short"
                      ? "#A32D2D"
                      : "#994010",
              }}
            >
              {derivedStatus === "ready"
                ? "✓ Ready"
                : derivedStatus === "short"
                  ? "⚠ Shortage"
                  : "↑ Excess logged"}
            </div>
            <div
              style={{
                fontSize: 13,
                marginTop: 2,
                color:
                  derivedStatus === "ready"
                    ? "#085041"
                    : derivedStatus === "short"
                      ? "#A32D2D"
                      : "#994010",
                opacity: 0.8,
              }}
            >
              Total cooked: {fmtQty(state.totalCooked, ci.outputType)}
              {state.readyTime ? ` · Last batch: ${state.readyTime}` : ""}
              {(state.recookCount || 0) > 0 &&
                ` · ${state.recookCount} recook${state.recookCount > 1 ? "s" : ""}`}
            </div>
          </div>
          {derivedStatus === "short" && (
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ textAlign: "right" }}>
                <div
                  style={{
                    fontSize: 11,
                    color: "#A32D2D",
                    opacity: 0.7,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Still to cook
                </div>
                <div
                  style={{ fontSize: 22, fontWeight: 700, color: "#A32D2D" }}
                >
                  {fmtQty(stillToCook, ci.outputType)}
                </div>
              </div>
              <button
                onClick={handleRecook}
                disabled={saving}
                style={{
                  padding: "10px 18px",
                  borderRadius: 8,
                  border: "none",
                  background: "#E24B4A",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {saving ? <Spinner size="sm" /> : "Prepare More"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ProductionSheetTab({ session, onSessionChange }) {
  const [entries, setEntries] = useState([]);
  const [chefs, setChefs] = useState([]);
  const [loading, setLoading] = useState(false);
  const today = moment().format("YYYY-MM-DD");

  const loadData = useCallback(
    async (s) => {
      setLoading(true);
      try {
        const [salesRes, productsRes, logsRes, chefsRes] = await Promise.all([
          axios.get(SALES_API, {
            params: { date: today, session: SESSION_LABEL[s] },
          }),
          axios.get(PRODUCTS_API),
          axios.get(LOG_API, { params: { date: today, session: s } }),
          axios.get(CHEFS_API),
        ]);

        const salesData = salesRes.data.success ? salesRes.data.data : [];
        const products = productsRes.data.success ? productsRes.data.data : [];
        const logs = logsRes.data.success ? logsRes.data.data : [];
        setChefs(chefsRes.data.success ? chefsRes.data.data : []);

        const orderMap = new Map();
        salesData.forEach((item) => {
          const total =
            (item.precutoffConfirmed || 0) + (item.windowFixedQty || 0);
          if (total > 0) {
            const ex = orderMap.get(item.foodName);
            orderMap.set(item.foodName, (ex || 0) + total);
          }
        });

        const cookableTargets = new Map();
        products.forEach((p) => {
          const qty = orderMap.get(p.name) || 0;
          if (!qty) return;
          p.components.forEach((c) => {
            const cid = c.cookableItemId?._id || c.cookableItemId;
            const ci = c.cookableItemId;
            const ex = cookableTargets.get(cid);
            if (ex) ex.target += qty * c.qty;
            else
              cookableTargets.set(cid, {
                cookableItem: ci,
                target: qty * c.qty,
              });
          });
        });

        const defaultLog = {
          status: "pending",
          totalCooked: 0,
          images: [],
          batches: [],
          recookCount: 0,
          readyTime: "",
          pendingAlert: null,
          pendingDiff: 0,
          chefId: null,
        };
        const result = Array.from(cookableTargets.entries()).map(
          ([cid, { cookableItem, target }]) => {
            const log = logs.find(
              (l) => (l.cookableItemId?._id || l.cookableItemId) === cid,
            ) || { ...defaultLog };
            return {
              cookableItem,
              target: Math.round(target * 1000) / 1000,
              log,
            };
          },
        );

        setEntries(result);
      } catch {
        toast.error("Failed to load production data");
      } finally {
        setLoading(false);
      }
    },
    [today],
  );

  useEffect(() => {
    loadData(session);
  }, [session, loadData]);

  const handleUpdate = async (entry, update) => {
    const cid = entry.cookableItem?._id || entry.cookableItem;
    const res = await axios.post(`${LOG_API}/upsert`, {
      date: today,
      session,
      cookableItemId: cid,
      chefId: update.chefId || null,
      target: entry.target,
      totalCooked: update.totalCooked ?? entry.log.totalCooked,
      status: update.status,
      images: update.images ?? entry.log.images,
      recookCount: update.recookCount ?? entry.log.recookCount,
      readyTime: update.readyTime ?? entry.log.readyTime,
      pendingAlert: update.pendingAlert ?? null,
      pendingDiff: update.pendingDiff ?? entry.log.pendingDiff,
      batches: update.batches ?? entry.log.batches ?? [],
    });
    if (res.data.success) {
      setEntries((prev) =>
        prev.map((e) =>
          (e.cookableItem?._id || e.cookableItem) === cid
            ? { ...e, log: res.data.data }
            : e,
        ),
      );
    }
  };

  const counts = {
    total: entries.length,
    ready: entries.filter((e) =>
      ["ready", "short", "excess"].includes(
        deriveStatus(e.target, e.log, e.cookableItem?.outputType),
      ),
    ).length,
    short: entries.filter(
      (e) =>
        deriveStatus(e.target, e.log, e.cookableItem?.outputType) === "short",
    ).length,
    excess: entries.filter(
      (e) =>
        deriveStatus(e.target, e.log, e.cookableItem?.outputType) === "excess",
    ).length,
  };

  const metricCards = [
    { label: "Total Items", val: counts.total, color: "#1A1A18" },
    { label: "Ready", val: counts.ready, color: "#1D9E75" },
    { label: "Shortage", val: counts.short, color: "#E24B4A" },
    { label: "Excess", val: counts.excess, color: "#994010" },
  ];

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h5 style={{ fontWeight: 700, marginBottom: 2 }}>Production Sheet</h5>
          <div style={{ fontSize: 13, color: "#888" }}>{today}</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {["b", "l", "d"].map((s) => (
            <button
              key={s}
              onClick={() => onSessionChange(s)}
              style={{
                padding: "8px 20px",
                borderRadius: 20,
                fontWeight: 500,
                fontSize: 14,
                cursor: "pointer",
                transition: "all 0.15s",
                ...(session === s
                  ? SESSION_BTN[s].active
                  : {
                      background: "#fff",
                      color: "#888",
                      border: "1.5px solid #e0e0e0",
                    }),
              }}
            >
              {SESSION_LABEL[s]}
            </button>
          ))}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4,1fr)",
          gap: 10,
          marginBottom: 20,
        }}
      >
        {metricCards.map((m) => (
          <div
            key={m.label}
            style={{
              background: "#fff",
              border: "1px solid #eee",
              borderRadius: 10,
              padding: "12px 16px",
            }}
          >
            <div
              style={{
                fontSize: 11,
                color: "#aaa",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                marginBottom: 4,
              }}
            >
              {m.label}
            </div>
            <div style={{ fontSize: 26, fontWeight: 700, color: m.color }}>
              {m.val}
            </div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner />
        </div>
      ) : entries.length === 0 ? (
        <div
          style={{
            background: "#fff",
            borderRadius: 12,
            border: "1px solid #eee",
            padding: 48,
            textAlign: "center",
            color: "#aaa",
          }}
        >
          <div style={{ fontSize: 40, marginBottom: 8 }}>📋</div>
          <div style={{ fontWeight: 500 }}>
            No production items for this session
          </div>
          <div style={{ fontSize: 13, marginTop: 4 }}>
            Make sure products are mapped in the Products tab and orders exist
            for today
          </div>
        </div>
      ) : (
        entries.map((entry, i) => (
          <ProductionCard
            key={entry.cookableItem?._id || i}
            entry={entry}
            chefs={chefs}
            onUpdate={(update) => handleUpdate(entry, update)}
          />
        ))
      )}
    </>
  );
}
